import { useState, useCallback, useRef } from 'react';

const useUndoRedo = ({ nodes, setNodes, edges, setEdges }) => {
    const [past, setPast] = useState([]);
    const [future, setFuture] = useState([]);

    // Refs to always have latest nodes/edges without causing re-renders on deps change
    const nodesRef = useRef(nodes);
    const edgesRef = useRef(edges);
    nodesRef.current = nodes;
    edgesRef.current = edges;

    // Snapshot: uses ref so it never gets stale and doesn't trigger useCallback invalidation
    const takeSnapshot = useCallback(() => {
        setPast((pastState) => [
            ...pastState,
            {
                nodes: JSON.parse(JSON.stringify(nodesRef.current)),
                edges: JSON.parse(JSON.stringify(edgesRef.current))
            },
        ]);
        setFuture([]);
    }, []); // No deps — refs are always current

    const undo = useCallback(() => {
        setPast((pastState) => {
            if (pastState.length === 0) return pastState;
            const newPast = [...pastState];
            const previousState = newPast.pop();

            setFuture((futureState) => [
                {
                    nodes: JSON.parse(JSON.stringify(nodesRef.current)),
                    edges: JSON.parse(JSON.stringify(edgesRef.current))
                },
                ...futureState,
            ]);

            setNodes(previousState.nodes);
            setEdges(previousState.edges);
            return newPast;
        });
    }, [setNodes, setEdges]); // Only stable setters needed

    const redo = useCallback(() => {
        setFuture((futureState) => {
            if (futureState.length === 0) return futureState;
            const newFuture = [...futureState];
            const nextState = newFuture.shift();

            setPast((pastState) => [
                ...pastState,
                {
                    nodes: JSON.parse(JSON.stringify(nodesRef.current)),
                    edges: JSON.parse(JSON.stringify(edgesRef.current))
                },
            ]);

            setNodes(nextState.nodes);
            setEdges(nextState.edges);
            return newFuture;
        });
    }, [setNodes, setEdges]); // Only stable setters needed

    const canUndo = past.length > 0;
    const canRedo = future.length > 0;

    return {
        undo,
        redo,
        takeSnapshot,
        canUndo,
        canRedo,
        past,
        future
    };
};

export default useUndoRedo;
